/* @jsx h */
import {
  computed,
  defineComponent,
  Fragment,
  h,
  onMounted,
  ref,
  watch,
} from "vue";
import { setTitle } from "@lemmonhq/orders";
import { ThemeSwitcher, useLocalization, SwissMade } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { useShop } from "@app/vue/providers/ShopConnection";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { SessionSibling } from "@app/server/trpc/routers/session";
import { isPrinterOnline } from "@app/utils/index";
import { api, instrumentation } from "@app/data/index";
import { getSupportedLanguagesForJurisdictions } from "@app/utils/supportedLanguages";
import { release, lemmonEnv } from "../../../version";
import { PrinterSetupWizard } from "@app/vue/pages/Devices";
import { MenuSection } from "./MenuSection";
import { MenuListItem } from "./MenuListItem";
import { RestaurantSwitcherImage } from "@app/vue/components/LayoutComponents/Navbar/RestaurantSwitcherImage";

export const MenuPage = defineComponent({
  name: "MenuPage",
  setup() {
    onMounted(() => setTitle("Menu"));

    const auth = useAuth();
    const shop = useShop();
    const trpc = useTrpc();
    const il8n = useLocalization();
    const { t } = il8n;

    let nativeBridge: any;
    try {
      nativeBridge = useNativeBridge();
    } catch (e) {
      // Not in a NativeBridge context, that's ok
    }

    const showPrinterSetupWizard = ref(false);
    const siblings = ref<SessionSibling[]>([]);
    const loadingSiblings = ref(true);
    const switchingUserId = ref<number | null>(null);
    const enteringGroupMode = ref(false);
    const loadingLang = ref<string | null>(null);

    const loadSiblings = async () => {
      loadingSiblings.value = true;
      try {
        siblings.value = await trpc.session.siblings.query();
      } catch (error) {
        console.error("Failed to load linked restaurants:", error);
        siblings.value = [];
      } finally {
        loadingSiblings.value = false;
      }
    };

    watch(
      () => auth.user.value?.id,
      (userId) => {
        if (userId) void loadSiblings();
        else {
          siblings.value = [];
          loadingSiblings.value = false;
        }
      },
      { immediate: true },
    );

    const switchRestaurant = async (userId: number) => {
      if (switchingUserId.value !== null) return;
      switchingUserId.value = userId;
      try {
        await trpc.session.switchShop.mutate({ userId });
        window.location.reload();
      } catch (error) {
        console.error("Failed to switch restaurant:", error);
        switchingUserId.value = null;
      }
    };

    const enterGroupMode = async () => {
      if (enteringGroupMode.value) return;
      enteringGroupMode.value = true;
      try {
        await trpc.session.enterGroupMode.mutate();
        window.location.assign("/group/dashboard");
      } catch (error) {
        console.error("Failed to enter Consolidated mode:", error);
        enteringGroupMode.value = false;
      }
    };

    const handleSetLocale = async (langId: string) => {
      loadingLang.value = langId;
      await il8n.setLocale(langId);
      loadingLang.value = null;
    };

    const handleLogout = async () => {
      try {
        const restaurantId = auth.user.value?.restaurant_id;
        if (restaurantId) {
          localStorage.setItem("shopId", restaurantId.toString());
        }
        await api.destroySession();
        instrumentation.reset();
        shop.bc.postMessage({ type: "logout" });
        if (nativeBridge) {
          await nativeBridge.logout();
        }
        window.location.href = "/";
      } catch (error) {
        console.error("Error during logout:", error);
      }
    };

    const allPrintersOnline = computed(() => {
      const printerStatuses = shop.printerStatuses.timestamps;
      const printers = shop.getPrinters();
      return printers?.every((el) => isPrinterOnline(printerStatuses, el.id));
    });

    const getAvailableLanguages = () =>
      getSupportedLanguagesForJurisdictions([
        auth.user.value?.restaurant.jurisdiction,
      ]);

    const canEnterGroupMode = computed(
      () =>
        auth.groupRole.value === "admin" ||
        (auth.groupRole.value === "employee" &&
          siblings.value.filter((sibling) => sibling.pos_role === "manager")
            .length > 1),
    );

    const appVersion = lemmonEnv?.app;

    return () => {
      const user = auth.user.value;
      if (!user) return null;

      const isManager = user.role === "manager";
      const posMode = isManager ? auth.posMode.value : true;
      const isStaging = import.meta.env.VITE_ENV === "staging";

      const isProduction = import.meta.env.VITE_ENV === "production";

      type Link = {
        to: string;
        icon: string;
        title: string;
        external?: boolean;
        badge?: number;
      };
      const section = (title: string, links: (Link | false)[]) => (
        <MenuSection title={title}>
          {links
            .filter((l): l is Link => !!l)
            .map((l) => (
              <MenuListItem
                key={l.to}
                to={l.to}
                icon={l.icon}
                title={l.title}
                external={l.external}
                badge={l.badge}
              />
            ))}
        </MenuSection>
      );

      // Mirrors the desktop nav and each page's subpage tabs; keep in sync when adding pages.
      const loyaltyLinks: (Link | false)[] = [
        {
          to: "/app/manager/loyalty",
          icon: "social_leaderboard",
          title: "promotions.nav.promotions",
        },
        {
          to: "/app/manager/loyalty/customers",
          icon: "person",
          title: "promotions.nav.customers",
        },
        {
          to: "/app/manager/loyalty/coins",
          icon: "toll",
          title: "promotions.coins.nav",
        },
        {
          to: "/app/manager/loyalty/memberships",
          icon: "card_membership",
          title: "promotions.memberships.nav",
        },
        {
          to: "/app/manager/loyalty/automations",
          icon: "bolt",
          title: "menu.labels.automations",
        },
        isStaging && {
          to: "/app/manager/loyalty/campaigns",
          icon: "campaign",
          title: "promotions.nav.campaigns",
        },
        isStaging && {
          to: "/app/manager/loyalty/gift-cards",
          icon: "card_giftcard",
          title: "menu.labels.gift_cards",
        },
        {
          to: "/app/manager/loyalty/wallet",
          icon: "wallet",
          title: "promotions.wallet.nav",
        },
      ];
      const catalogLinks: Link[] = isProduction
        ? [
            {
              to: "/manager/products",
              icon: "category",
              title: "manager.nav.catalog",
              external: true,
            },
          ]
        : [
            {
              to: "/app/catalog_editor/overview",
              icon: "space_dashboard",
              title: "catalog.nav.overview",
            },
            {
              to: "/app/catalog_editor/services",
              icon: "category",
              title: "catalog.nav.services",
            },
            {
              to: "/app/catalog_editor/categories",
              icon: "folder",
              title: "catalog.nav.categories",
            },
            {
              to: "/app/catalog_editor/products",
              icon: "inventory_2",
              title: "catalog.nav.products",
            },
            {
              to: "/app/catalog_editor/extras",
              icon: "add_circle",
              title: "catalog.nav.extras",
            },
          ];
      const workdayLinks: Link[] = [
        {
          to: "/app/workday/myday",
          icon: "today",
          title: "workday.nav.my_day",
        },
        {
          to: "/app/workday/procedures",
          icon: "fact_check",
          title: "workday.nav.procedures",
        },
        { to: "/app/workday/prep", icon: "skillet", title: "workday.nav.prep" },
        {
          to: "/app/workday/tasks",
          icon: "task_alt",
          title: "workday.nav.tasks",
        },
        {
          to: "/app/workday/rota",
          icon: "calendar_view_week",
          title: "workday.nav.rota",
        },
        { to: "/app/workday/team", icon: "groups", title: "workday.nav.team" },
      ];
      const inventoryLinks: Link[] = [
        {
          to: "/app/inventory/overview",
          icon: "space_dashboard",
          title: "inventory.nav.overview",
        },
        {
          to: "/app/inventory/stock",
          icon: "inventory",
          title: "inventory.nav.stock",
        },
        {
          to: "/app/inventory/counts",
          icon: "checklist",
          title: "inventory.nav.counts",
        },
        {
          to: "/app/inventory/orders",
          icon: "shopping_cart",
          title: "inventory.nav.orders",
        },
        {
          to: "/app/inventory/deliveries",
          icon: "local_shipping",
          title: "inventory.nav.deliveries",
        },
        {
          to: "/app/inventory/invoices",
          icon: "request_quote",
          title: "inventory.nav.invoices",
        },
        {
          to: "/app/inventory/suppliers",
          icon: "storefront",
          title: "inventory.nav.suppliers",
        },
        {
          to: "/app/inventory/waste",
          icon: "delete_sweep",
          title: "inventory.nav.waste",
        },
        {
          to: "/app/inventory/margins",
          icon: "monitoring",
          title: "inventory.nav.margins",
        },
        {
          to: "/app/inventory/marketplace",
          icon: "travel_explore",
          title: "inventory.nav.marketplace",
        },
      ];

      const reservationsSection = section("nav.labels.reservations", [
        {
          to: "/app/manager/reservations",
          icon: "table_restaurant",
          title: "reservations.nav.requests",
          badge: shop.pendingReservationRequestsCount.value,
        },
        {
          to: "/app/manager/reservations/customers",
          icon: "group",
          title: "reservations.customers.title",
        },
        {
          to: "/app/manager/reservations/schedule",
          icon: "calendar_month",
          title: "reservations.nav.schedule",
        },
        {
          to: "/app/manager/reservations/history",
          icon: "history",
          title: "reservations.nav.history",
        },
        {
          to: "/app/manager/reservations/events",
          icon: "celebration",
          title: "reservations.nav.events",
        },
        {
          to: "/app/tables?mode=reservations",
          icon: "map",
          title: "reservations.nav.floor_plan",
        },
      ]);

      return (
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-base-100">
          {/* Sticky collapsed profile bar */}
          <div
            style={{ paddingTop: "calc(0.75rem)" }}
            class="sticky top-0 z-10 flex items-center gap-3 border-b border-base-content/[0.06] bg-base-100/95 px-4 pb-3 backdrop-blur-md"
          >
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-base-content/10 bg-base-content/10 text-[11px] font-semibold text-base-content/70">
              {user.profile.name
                .split(" ")
                .slice(0, 2)
                .map((part) => part[0])
                .join("")
                .toUpperCase()}
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-[13.5px] font-semibold leading-tight text-base-content/90">
                {user.profile.name}
              </div>
              <div class="truncate text-[11px] leading-tight text-base-content/45">
                {user.restaurant.name}
              </div>
            </div>
            {user.role !== "admin" ? (
              <div
                class="flex items-center gap-1.5"
                title={
                  allPrintersOnline.value
                    ? t("nav.quick_links.printers_online")
                    : t("nav.quick_links.printers_offline")
                }
              >
                <span
                  class={`material-symbols-outlined text-[17px] ${
                    allPrintersOnline.value ? "text-success" : "text-warning"
                  }`}
                >
                  print
                </span>
                <span
                  class={`h-1.5 w-1.5 shrink-0 rounded-full ${
                    allPrintersOnline.value
                      ? "bg-success"
                      : "bg-warning animate-pulse"
                  }`}
                />
              </div>
            ) : null}
          </div>

          {/* Consolidated mode */}
          {canEnterGroupMode.value ? (
            <MenuSection>
              <MenuListItem
                icon="domain"
                title="admin.nav.admin_mode"
                iconClass="text-primary"
                onClick={() => void enterGroupMode()}
              />
            </MenuSection>
          ) : null}

          {/* Switch restaurant */}
          {loadingSiblings.value ? (
            <MenuSection title="nav.quick_links.switch_restaurant">
              {[0, 1].map((i) => (
                <div key={i} class="flex w-full items-center gap-3 px-4 py-3">
                  <span class="h-6 w-6 shrink-0 animate-pulse rounded-md bg-base-content/10" />
                  <span class="h-3.5 w-32 max-w-[60%] animate-pulse rounded bg-base-content/10" />
                </div>
              ))}
            </MenuSection>
          ) : siblings.value.length > 1 ? (
            <MenuSection title="nav.quick_links.switch_restaurant">
              {siblings.value.map((sibling) => (
                <button
                  key={sibling.user_id}
                  type="button"
                  class="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-base-content/5"
                  onClick={() =>
                    !sibling.is_current &&
                    void switchRestaurant(sibling.user_id)
                  }
                >
                  {switchingUserId.value === sibling.user_id ? (
                    <span class="loading loading-spinner loading-xs shrink-0" />
                  ) : (
                    <RestaurantSwitcherImage
                      name={sibling.restaurant_name}
                      url={sibling.image_url}
                    />
                  )}
                  <span
                    class={`min-w-0 flex-1 truncate text-[14.5px] font-medium ${
                      sibling.is_current
                        ? "text-primary"
                        : "text-base-content/80"
                    }`}
                  >
                    {sibling.restaurant_name}
                  </span>
                  {sibling.is_current ? (
                    <span class="material-symbols-outlined shrink-0 text-[18px] text-primary">
                      check
                    </span>
                  ) : null}
                </button>
              ))}
            </MenuSection>
          ) : null}

          {/* Navigation */}
          {posMode ? (
            <Fragment>
              <MenuSection title="menu.section.general">
                <MenuListItem
                  to="/app/pos"
                  icon="computer"
                  title="nav.core.pos"
                />
                <MenuListItem
                  to="/app/tables"
                  icon="table_restaurant"
                  title="nav.core.tables"
                />
                <MenuListItem
                  to="/app/orders"
                  icon="order_approve"
                  title="nav.core.orders"
                />
                <MenuListItem
                  to="/app/products"
                  icon="fork_spoon"
                  title="nav.core.products"
                />
              </MenuSection>

              {reservationsSection}

              <MenuSection title="reports.nav.sales_dashboard">
                <MenuListItem
                  to="/app/reports/live"
                  icon="bolt"
                  title="reports.nav.live"
                />
                <MenuListItem
                  to="/app/reports"
                  icon="show_chart"
                  title="reports.nav.sales_dashboard"
                />
                <MenuListItem
                  to="/app/reports/transactions?responsive=true"
                  icon="history"
                  title="reports.nav.payment_history"
                />
                <MenuListItem
                  to="/app/reports/tips"
                  icon="savings"
                  title="reports.nav.tips"
                />
                <MenuListItem
                  to="/app/reports/closing_reports"
                  icon="sunny"
                  title="reports.nav.closing_reports"
                />
                <MenuListItem
                  to="/app/reports/shifts"
                  icon="person"
                  title="reports.nav.shifts"
                />
                {isManager ? (
                  <Fragment>
                    <MenuListItem
                      to="/app/reports/reservations"
                      icon="event_available"
                      title="reports.nav.reservations"
                    />
                    <MenuListItem
                      to="/app/reports/dataroom"
                      icon="database"
                      title="reports.nav.dataroom"
                    />
                    {isStaging ? (
                      <MenuListItem
                        to="/app/reports/chart_of_accounts"
                        icon="account_tree"
                        title="reports.nav.chart_of_accounts"
                      />
                    ) : null}
                    <MenuListItem
                      to="/app/reports/export"
                      icon="file_export"
                      title="reports.nav.export"
                    />
                  </Fragment>
                ) : null}
              </MenuSection>

              <MenuSection>
                <MenuListItem
                  icon="print_add"
                  title="menu.labels.printer_setup"
                  onClick={() => (showPrinterSetupWizard.value = true)}
                />
              </MenuSection>
            </Fragment>
          ) : (
            <Fragment>
              <MenuSection title="nav.labels.reports">
                <MenuListItem
                  to="/app/reports/live"
                  icon="bolt"
                  title="reports.nav.live"
                />
                <MenuListItem
                  to="/app/reports"
                  icon="show_chart"
                  title="reports.nav.sales_dashboard"
                />
                <MenuListItem
                  to="/app/reports/transactions?responsive=true"
                  icon="history"
                  title="reports.nav.payment_history"
                />
                <MenuListItem
                  to="/app/reports/tips"
                  icon="savings"
                  title="reports.nav.tips"
                />
                <MenuListItem
                  to="/app/reports/closing_reports"
                  icon="sunny"
                  title="reports.nav.closing_reports"
                />
                <MenuListItem
                  to="/app/reports/revenue"
                  icon="stacked_line_chart"
                  title="reports.nav.revenue"
                />
                <MenuListItem
                  to="/app/reports/shifts"
                  icon="person"
                  title="reports.nav.shifts"
                />
                <MenuListItem
                  to="/app/reports/weekly_heatmap"
                  icon="date_range"
                  title="reports.nav.weekly_sales"
                />
                <MenuListItem
                  to="/app/reports/reservations"
                  icon="event_available"
                  title="reports.nav.reservations"
                />
                <MenuListItem
                  to="/app/reports/dataroom"
                  icon="database"
                  title="reports.nav.dataroom"
                />
                {isStaging ? (
                  <MenuListItem
                    to="/app/reports/chart_of_accounts"
                    icon="account_tree"
                    title="reports.nav.chart_of_accounts"
                  />
                ) : null}
                <MenuListItem
                  to="/app/reports/export"
                  icon="file_export"
                  title="reports.nav.export"
                />
              </MenuSection>

              {reservationsSection}

              <MenuSection title="menu.section.general">
                {isProduction ? null : (
                  <MenuListItem
                    to="/app/intelligence"
                    icon="auto_awesome"
                    title="menu.labels.agents"
                  />
                )}
                <MenuListItem
                  to="/app/tables"
                  icon="table_restaurant"
                  title="nav.core.tables"
                />
                <MenuListItem
                  to="/app/devices"
                  icon="devices"
                  title="nav.core.devices"
                />
                <MenuListItem
                  icon="print_add"
                  title="menu.labels.printer_setup"
                  onClick={() => (showPrinterSetupWizard.value = true)}
                />
              </MenuSection>

              {section("nav.labels.loyalty", loyaltyLinks)}
              {section("manager.nav.catalog", catalogLinks)}
              {isProduction
                ? null
                : section("nav.labels.workday", workdayLinks)}
              {isProduction
                ? null
                : section("nav.labels.inventory", inventoryLinks)}

              <MenuSection title="nav.labels.settings">
                <MenuListItem
                  to="/app/settings"
                  icon="tune"
                  title="settings.nav.title"
                />
                <MenuListItem
                  to="/app/settings/profile"
                  icon="restaurant"
                  title="settings.nav.profile"
                />
                <MenuListItem
                  to="/app/settings/reservations"
                  icon="calendar_month"
                  title="settings.nav.reservations"
                />
                <MenuListItem
                  to="/app/settings/assets"
                  icon="perm_media"
                  title="settings.nav.assets"
                />
                <MenuListItem
                  to="/app/settings/discount-types"
                  icon="local_offer"
                  title="settings.discount_types_page.page_title"
                />
                <MenuListItem
                  to="/app/settings/payments"
                  icon="payment"
                  title="settings.nav.payments"
                />
                <MenuListItem
                  to="/app/settings/print-templates"
                  icon="receipt_long"
                  title="menu.labels.print_templates"
                />
                <MenuListItem
                  to="/app/settings/audit"
                  icon="history"
                  title="menu.labels.audit"
                />
                {isStaging ? (
                  <Fragment>
                    <MenuListItem
                      to="/app/settings/staff"
                      icon="group"
                      title="settings.nav.staff"
                    />
                    <MenuListItem
                      to="/app/settings/taxes"
                      icon="money"
                      title="settings.nav.taxes"
                    />
                  </Fragment>
                ) : null}
              </MenuSection>
            </Fragment>
          )}

          {/* Preferences */}
          <MenuSection title="admin.nav.language">
            {getAvailableLanguages().map((lang) => (
              <button
                key={lang.id}
                type="button"
                class="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-base-content/5"
                onClick={() => handleSetLocale(lang.id)}
              >
                {loadingLang.value === lang.id ? (
                  <span class="loading loading-spinner loading-xs shrink-0" />
                ) : (
                  <span class="material-symbols-outlined shrink-0 text-[19px] text-base-content/50">
                    {il8n.locale.value === lang.id
                      ? "radio_button_checked"
                      : "radio_button_unchecked"}
                  </span>
                )}
                <span class="min-w-0 flex-1 truncate text-[14.5px] font-medium text-base-content/80">
                  {lang.name}
                </span>
              </button>
            ))}
          </MenuSection>

          {isStaging ? (
            <div class="px-4 pt-5">
              <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
                {t("admin.nav.theme")}
              </div>
              <div class="overflow-hidden rounded-2xl border border-base-content/[0.06] bg-base-200/60 px-2 py-1">
                <ThemeSwitcher />
              </div>
            </div>
          ) : null}

          {/* Logout */}
          <div class="px-4 pt-5">
            <button
              type="button"
              onClick={handleLogout}
              class="flex w-full items-center justify-center gap-2 rounded-2xl border border-error/15 bg-error/5 px-4 py-3.5 text-[14.5px] font-semibold text-error/80 active:bg-error/10"
            >
              <span class="material-symbols-outlined text-[18px]">logout</span>
              {t("admin.common.logout")}
            </button>
          </div>

          {/* Footer */}
          <div class="flex flex-col items-center gap-2 px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-6">
            <SwissMade width={40} />
            <div class="text-center text-[10px] leading-tight text-base-content/30">
              {release}
              {appVersion?.version ? ` · v${appVersion.version}` : ""}
              {import.meta.env.VITE_ENV
                ? ` · ${(import.meta.env.VITE_ENV as string).toUpperCase()}`
                : ""}
            </div>
          </div>

          {showPrinterSetupWizard.value && (
            <PrinterSetupWizard
              onClose={() => (showPrinterSetupWizard.value = false)}
              onSuccess={() => (showPrinterSetupWizard.value = false)}
            />
          )}
        </div>
      );
    };
  },
});
